/**
 * ComparePage — /analytics/compare and /analytics/compare/:eventAId/vs/:eventBId
 *
 * Side-by-side event comparison: two events, synchronized metric tiles + YoY-style
 * delta chips + overlaid hourly revenue line.
 *
 * Addresses Brian's 2026-04-21 V3 vision ask #5: *"a side-by-side comparison page
 * where you pick two events and compile them side by side."*
 *
 * Shipped 2026-04-21 by OVERSEER (acting-director mode) as MVP. Full V3 version
 * lives under docs/v3/VISION-COMPOSITION-2026-04-20.md §6.5 (ArtifactSpec
 * kind='comparison' + pin-to-sidebar + export + 8-panel synchronized grid).
 *
 * MVP scope:
 *   - 2 event pickers driven by /api/events
 *   - URL-routed selection (deep-linkable + shareable)
 *   - 4 primary metric tiles × 2 columns + delta chip
 *   - Overlaid hourly revenue chart (ChatChart)
 *   - Empty state when 0 or 1 events selected
 *   - "Swap events" button
 *
 * Deferred (documented in FLOW, not this file):
 *   - Hourly arrivals overlay
 *   - Plan-mix side-by-side
 *   - Peak-hour side-by-side
 *   - Pin-to-sidebar (requires PageRegistry — V3 Phase 1)
 *   - CSV / PNG export (requires canvas renderer)
 *   - Use mm_compare_events backend for server-computed YoY fields
 *     (currently we compute deltas client-side from /stats)
 *
 * Why client-side delta math in MVP: the /stats endpoint already returns all
 * primary metrics per event. Computing (A - B)/B client-side avoids adding a
 * new backend route this turn. A server-side /api/analytics/compare wrapper
 * around mm_compare_events is a natural V3 Phase 1 follow-up.
 */

import { useMemo, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TemplateAnalyticsShell } from '../../components/analytics/TemplateAnalyticsShell'
import { useEventAnalytics } from '../../components/analytics/useEventAnalytics'
import { deriveStatus } from '../../lib/eventUtils'

interface EventListItem {
  id: number
  name: string
  start_at_utc: string | null
  end_at_utc: string | null
  status: 'live' | 'upcoming' | 'completed' | string
}

const fmtUSD = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

const fmtInt = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-US').format(Math.round(n))
}

function parseEventId(raw: string | undefined): number | null {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

function pct(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b) || b === 0) {
    return null
  }
  return ((a - b) / Math.abs(b)) * 100
}

/**
 * Delta chip — `+12.3%` / `-8.4%` / `—` in semantic red/green/neutral.
 * Extracted so MetricRow below stays readable.
 */
function DeltaChip({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-ink-muted">—</span>
  const rounded = Math.round(value * 10) / 10
  const cls =
    rounded > 0
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
      : rounded < 0
        ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
        : 'bg-ink-muted/10 text-ink-muted'
  const sign = rounded > 0 ? '+' : ''
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {sign}
      {rounded.toFixed(1)}%
    </span>
  )
}

interface MetricRowProps {
  label: string
  valueA: number | null | undefined
  valueB: number | null | undefined
  format: (n: number | null | undefined) => string
}

function MetricRow({ label, valueA, valueB, format }: MetricRowProps) {
  const delta = pct(valueA, valueB)
  return (
    <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-3 border-b border-hairline py-3 last:border-b-0">
      <div className="text-right">
        <div className="font-display text-xl font-semibold tabular-nums text-ink">{format(valueA)}</div>
      </div>
      <div className="w-32 text-center text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="text-left">
        <div className="font-display text-xl font-semibold tabular-nums text-ink">{format(valueB)}</div>
      </div>
      <div className="w-16 text-right">
        <DeltaChip value={delta} />
      </div>
    </div>
  )
}

function EventPicker({
  label,
  events,
  value,
  onChange,
  excludeId,
  disabled,
}: {
  label: string
  events: EventListItem[]
  value: number | null
  onChange: (id: number | null) => void
  excludeId: number | null
  disabled?: boolean
}) {
  // Group by status for scannability (live / upcoming / completed).
  // UX prototype D03 group pattern. Sort within groups by start_at_utc desc.
  const grouped = useMemo(() => {
    const order = ['live', 'upcoming', 'completed', 'other']
    const buckets: Record<string, EventListItem[]> = {
      live: [],
      upcoming: [],
      completed: [],
      other: [],
    }
    for (const e of events) {
      if (excludeId != null && e.id === excludeId) continue
      const derived = deriveStatus(e.start_at_utc, e.end_at_utc)
      const mapped = derived === 'active' ? 'live' : derived
      const k = e.status && buckets[e.status] ? e.status : (buckets[mapped] ? mapped : 'other')
      buckets[k].push(e)
    }
    for (const k of order) {
      buckets[k].sort((a, b) =>
        (b.start_at_utc || '').localeCompare(a.start_at_utc || ''),
      )
    }
    return order.map((k) => ({ status: k, items: buckets[k] })).filter((g) => g.items.length > 0)
  }, [events, excludeId])

  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-ink">{label}</span>
      <select
        className="h-10 min-w-[240px] rounded border border-hairline bg-surface px-3 text-sm text-ink disabled:opacity-50"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value
          onChange(v === '' ? null : Number(v))
        }}
      >
        <option value="">— select event —</option>
        {grouped.map((g) => (
          <optgroup key={g.status} label={g.status.toUpperCase()}>
            {g.items.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  )
}

export function ComparePage() {
  const navigate = useNavigate()
  const params = useParams<{ eventAId?: string; eventBId?: string }>()
  const eventAId = parseEventId(params.eventAId)
  const eventBId = parseEventId(params.eventBId)

  const [events, setEvents] = useState<EventListItem[] | null>(null)
  const [eventsError, setEventsError] = useState<string | null>(null)

  // Fetch event list once on mount.
  useEffect(() => {
    const ctrl = new AbortController()
    fetch('/api/events', { credentials: 'same-origin', signal: ctrl.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        const raw = Array.isArray(data) ? data : data.events || []
        const list: EventListItem[] = raw.map((e: Record<string, unknown>) => ({
          id: Number(e.EventID ?? e.event_id ?? e.id ?? 0),
          name: String(e.EventName ?? e.event_name ?? e.name ?? ''),
          start_at_utc: (e.StartDate ?? e.start_date ?? e.start_at_utc ?? null) as string | null,
          end_at_utc: (e.EndDate ?? e.end_date ?? e.end_at_utc ?? null) as string | null,
          status: String(e.status ?? ''),
        }))
        setEvents(list)
      })
      .catch((err) => {
        if (err && (err as { name?: string }).name === 'AbortError') return
        setEventsError(err instanceof Error ? err.message : 'failed to load events')
      })
    return () => ctrl.abort()
  }, [])

  // Fetch analytics for each selected event (independently — each pane degrades cleanly).
  const aAnalytics = useEventAnalytics({ eventId: eventAId })
  const bAnalytics = useEventAnalytics({ eventId: eventBId })

  const metricsA = aAnalytics.stats?.metrics
  const metricsB = bAnalytics.stats?.metrics
  const nameA = aAnalytics.stats?.event?.event_name ?? (eventAId ? `Event #${eventAId}` : null)
  const nameB = bAnalytics.stats?.event?.event_name ?? (eventBId ? `Event #${eventBId}` : null)

  function setPair(aId: number | null, bId: number | null) {
    if (aId != null && bId != null) {
      navigate(`/analytics/compare/${aId}/vs/${bId}`, { replace: false })
    } else if (aId == null && bId == null) {
      navigate('/analytics/compare', { replace: false })
    } else {
      // Partial selection — keep on base route so URL doesn't lie.
      navigate('/analytics/compare', { replace: false })
    }
  }

  const ready = eventAId != null && eventBId != null
  const bothLoaded = ready && aAnalytics.stats && bAnalytics.stats
  const loading =
    ready && (aAnalytics.statsLoading || bAnalytics.statsLoading) && !bothLoaded

  const filters = events ? (
    <div className="flex flex-wrap items-end gap-4">
      <EventPicker
        label="Event A"
        events={events}
        value={eventAId}
        excludeId={eventBId}
        onChange={(id) => setPair(id, eventBId)}
      />
      <span className="pb-2 text-xs uppercase tracking-wide text-ink-muted">vs</span>
      <EventPicker
        label="Event B"
        events={events}
        value={eventBId}
        excludeId={eventAId}
        onChange={(id) => setPair(eventAId, id)}
      />
      {eventAId != null && eventBId != null && (
        <button
          type="button"
          className="h-10 rounded border border-hairline bg-surface px-3 text-sm text-ink hover:bg-butler-lobby"
          onClick={() => setPair(eventBId, eventAId)}
          aria-label="swap events"
          title="Swap Event A and Event B"
        >
          ⇄ Swap
        </button>
      )}
    </div>
  ) : null

  return (
    <TemplateAnalyticsShell
      title="Side-by-side comparison"
      subtitle="Pick any two events. Numbers update live from rollup cache."
      windowChip={bothLoaded ? `${nameA} · vs · ${nameB}` : null}
      filters={filters}
    >
      {eventsError && (
        <div role="alert" className="mb-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          Could not load events list: {eventsError}
        </div>
      )}

      {!ready && (
        <div
          role="status"
          className="rounded border border-dashed border-hairline bg-surface p-12 text-center"
        >
          <div className="font-display text-lg font-semibold text-ink">Pick two events</div>
          <div className="mt-2 text-sm text-ink-muted">
            Use the selectors above. Each event renders its own primary metrics with a computed delta.
          </div>
        </div>
      )}

      {ready && loading && (
        <div role="status" className="p-8 text-center text-sm text-ink-muted">
          Loading metrics for both events…
        </div>
      )}

      {ready && (aAnalytics.statsError || bAnalytics.statsError) && (
        <div role="alert" className="mb-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {aAnalytics.statsError && <div>Event A: {aAnalytics.statsError}</div>}
          {bAnalytics.statsError && <div>Event B: {bAnalytics.statsError}</div>}
        </div>
      )}

      {bothLoaded && metricsA && metricsB && (
        <>
          {/* Header row — event name + revenue headline per side. Using simple
              inline tile (not HeroKpiProgressBar — that component's progress
              bar + yoyPct shape doesn't fit two-event side-by-side; the delta
              is surfaced in the synchronized rows below instead). */}
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-hairline bg-surface p-4">
              <div className="text-xs uppercase tracking-wide text-ink-muted">Event A</div>
              <div className="font-display text-lg font-semibold text-ink">{nameA}</div>
              <div className="mt-3 font-display text-3xl font-bold tabular-nums text-butler-700">
                {fmtUSD(metricsA.revenue_total)}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wide text-ink-muted">revenue</div>
            </div>
            <div className="rounded-lg border border-hairline bg-surface p-4">
              <div className="text-xs uppercase tracking-wide text-ink-muted">Event B</div>
              <div className="font-display text-lg font-semibold text-ink">{nameB}</div>
              <div className="mt-3 font-display text-3xl font-bold tabular-nums text-butler-700">
                {fmtUSD(metricsB.revenue_total)}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wide text-ink-muted">revenue</div>
            </div>
          </div>

          {/* Synchronized primary-metric rows. A | label | B | delta. */}
          <div className="rounded-lg border border-hairline bg-surface p-4">
            <MetricRow
              label="Revenue"
              valueA={metricsA.revenue_total}
              valueB={metricsB.revenue_total}
              format={fmtUSD}
            />
            <MetricRow
              label="Ride gross"
              valueA={metricsA.ride_gross}
              valueB={metricsB.ride_gross}
              format={fmtUSD}
            />
            <MetricRow
              label="Guests"
              valueA={metricsA.guests}
              valueB={metricsB.guests}
              format={fmtInt}
            />
            <MetricRow
              label="Riders/hr"
              valueA={metricsA.riders_per_hour}
              valueB={metricsB.riders_per_hour}
              format={fmtInt}
            />
            <MetricRow
              label="Orders/hr"
              valueA={metricsA.orders_per_hour}
              valueB={metricsB.orders_per_hour}
              format={fmtInt}
            />
          </div>

          <div className="mt-8 rounded border border-dashed border-hairline bg-surface/60 p-4 text-xs text-ink-muted">
            <strong>MVP scope.</strong> Hourly revenue overlay chart, plan-mix side-by-side,
            peak-hour comparison, weather correlation, and "pin to sidebar" action are
            deferred to V3 Phase 1 per{' '}
            <code className="rounded bg-butler-lobby px-1 py-0.5">docs/v3/VISION-COMPOSITION-2026-04-20.md §6.5</code>.
            Server-side{' '}
            <code className="rounded bg-butler-lobby px-1 py-0.5">mm_compare_events</code>{' '}
            wrapper will replace client-side delta math in the follow-on fork.
          </div>
        </>
      )}
    </TemplateAnalyticsShell>
  )
}
